<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import resolveUrl from '@/services/resolveUrl';
import linkify from '@/services/linkify';
import AudioPlayer from './AudioPlayer.vue';
import ContatoModal from '@/components/crm/ContatoModal.vue';
import IniciarConversaModal from './IniciarConversaModal.vue';

export default {
  name: 'MensagemItem',
  components: { AudioPlayer, ContatoModal, IniciarConversaModal },
  props: {
    mensagem: { type: Object, required: true },
  },
  computed: {
    ...mapGetters('atendimento', ['mensagensDaAtiva']),
    eEntrada() { return this.mensagem.direcao === 'entrada'; },
    contatosCompartilhados() {
      return this.midiaMeta.contacts || [];
    },
    eMensagemContato() {
      return this.mensagem.tipo === 'contato'
        || (Array.isArray(this.contatosCompartilhados) && this.contatosCompartilhados.length > 0);
    },
    eSistema() {
      if (this.eMensagemContato) return false;
      return this.mensagem.direcao === 'sistema' || this.mensagem.tipo === 'sistema';
    },
    horario() {
      const data = this.mensagem.recebida_em || this.mensagem.enviada_em || this.mensagem.created_at;
      if (!data) return '';
      try { return new Date(data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); }
      catch (_e) { return ''; }
    },
    iconeStatus() {
      // Check ("vinzinho") em tom NEUTRO — herda a cor do contexto (cinza no
      // balão/rodapé, branco sobre o overlay escuro da mídia), igual aos grupos
      // e ao WhatsApp Web. `cor: null` = sem override inline → herda. Falha
      // continua vermelha pra distinção semântica.
      const map = {
        pendente:  { icone: 'ri-time-line',         cor: null,      titulo: 'Enviando...' },
        enviada:   { icone: 'ri-check-line',        cor: null,      titulo: 'Enviada' },
        entregue:  { icone: 'ri-check-double-line', cor: null,      titulo: 'Entregue' },
        lida:      { icone: 'ri-check-double-line', cor: null,      titulo: 'Lida' },
        falha:     { icone: 'ri-error-warning-line',cor: '#e74c3c', titulo: 'Falha no envio' },
      };
      return map[this.mensagem.status_envio] || null;
    },
    midiaUrl() {
      const raw = this.mensagem.midia_url;
      if (!raw) return null;
      if (raw.startsWith('http')) return raw;
      const base = process.env.VUE_APP_APIURL || '';
      return `${base}${raw}`;
    },
    midiaMeta() { return this.mensagem.midia_meta || this.mensagem.midiaMeta || {}; },
    nomeArquivo() { return this.midiaMeta.filename || this.midiaMeta.name || 'arquivo'; },
    tamanhoArquivo() {
      const s = this.midiaMeta.size;
      if (!s) return '';
      if (s < 1024) return `${s} B`;
      if (s < 1024 * 1024) return `${(s / 1024).toFixed(1)} KB`;
      return `${(s / 1024 / 1024).toFixed(1)} MB`;
    },
    linkMaps() {
      const m = this.midiaMeta;
      if (!m.latitude || !m.longitude) return null;
      return `https://www.google.com/maps?q=${m.latitude},${m.longitude}`;
    },
    // ── Cards padronizados (mesmos padrões dos grupos) ────────────────
    linkPreview() { return this.midiaMeta.link_preview || null; },
    temImagemVideo() {
      return ['imagem', 'video'].includes(this.mensagem.tipo) && Boolean(this.midiaUrl);
    },
    temCoordenadas() {
      const m = this.midiaMeta;
      return m && m.latitude != null && m.longitude != null
        && !Number.isNaN(Number(m.latitude)) && !Number.isNaN(Number(m.longitude));
    },
    temLocalizacaoMapa() {
      return this.mensagem.tipo === 'localizacao' && this.temCoordenadas;
    },
    hrefLocalizacao() {
      return this.midiaMeta.url || this.linkMaps || '#';
    },
    temLinkCard() {
      return this.mensagem.tipo === 'texto' && Boolean(this.linkPreview);
    },
    // Hora sobre fundo ESCURO (mídia/mapa): imagem/vídeo sem legenda + mapa.
    overlayHoraDark() {
      if (this.temLocalizacaoMapa) return true;
      if (this.temImagemVideo && !this.mensagem.conteudo) return true;
      return false;
    },
    // Hora sobre card de corpo claro (link preview) — reduz a altura do card.
    overlayHoraSub() { return this.temLinkCard; },
    // Footer flutuante embaixo some quando há overlay (mídia/mapa/link) ou áudio.
    mostrarFooter() {
      if (this.mensagem.tipo === 'audio' && this.midiaUrl) return false;
      if (this.overlayHoraDark) return false;
      if (this.overlayHoraSub) return false;
      return true;
    },
    iconeMidia() {
      const map = {
        imagem: 'ri-image-line',
        video: 'ri-video-line',
        audio: 'ri-volume-up-line',
        documento: 'ri-file-line',
      };
      return map[this.mensagem.tipo] || 'ri-file-line';
    },
    rotuloMidia() {
      const map = {
        imagem: 'Imagem',
        video: 'V\u00EDdeo',
        audio: '\u00C1udio',
        documento: 'Documento',
      };
      return map[this.mensagem.tipo] || 'M\u00EDdia';
    },
    // Indica se o contato compartilhado nesta mensagem é o MESMO contato
    // da conversa que o atendente está visualizando. Nesse caso, clicar no
    // botão "abrir conversa" não faz sentido (já estamos nele).
    eContatoDaConversaAtiva() {
      if (!this.contatoBaseId) return false;
      const conversaAtiva = this.$store.getters['atendimento/conversaAtiva'];
      return Boolean(conversaAtiva && conversaAtiva.contato_id === this.contatoBaseId);
    },
    contatosFormatados() {
      return (this.contatosCompartilhados || []).map((contato, index) => {
        const nome = (contato?.name && (
          contato.name.formatted_name
          || [contato.name.first_name, contato.name.last_name].filter(Boolean).join(' ')
        )) || contato?.org?.company || `Contato ${index + 1}`;

        const telefones = Array.isArray(contato?.phones)
          ? contato.phones
            .map((item) => this._formatarTelefoneContato(item?.phone || item?.wa_id || ''))
            .filter(Boolean)
          : [];

        const emails = Array.isArray(contato?.emails)
          ? contato.emails.map((item) => item?.email).filter(Boolean)
          : [];

        const empresa = contato?.org?.company || null;
        const iniciais = nome.split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((parte) => parte.charAt(0).toUpperCase())
          .join('') || '?';

        return {
          nome,
          telefones,
          emails,
          empresa,
          iniciais,
        };
      }).filter((contato) => contato.nome || contato.telefones.length || contato.emails.length || contato.empresa);
    },
    // Card de contato compartilhado — duas linhas:
    //   1) "Contato compartilhado"
    //   2) "Nome Sobrenome — telefone"
    // Também devolve `copiar` pra ser usado no menu Copiar do balão.
    infoContato() {
      if (!this.contatosFormatados.length) {
        return { titulo: '', subtitulo: '', copiar: this.mensagem.conteudo || '' };
      }
      const c = this.contatosFormatados[0];
      const telefone = (c.telefones && c.telefones[0]) || '';
      const subtitulo = telefone ? `${c.nome} — ${telefone}` : c.nome;
      const copiar = telefone ? `${c.nome}\n${telefone}` : c.nome;
      return { titulo: 'Contato compartilhado', subtitulo, copiar };
    },
    mostrarTextoPrincipal() {
      if (this.mensagem.tipo === 'template') return false;
      // Localização: o card (mapa + nome/endereço) já mostra tudo; o conteudo
      // costuma ser só "lat,lng" — não repetir a coordenada como texto.
      if (this.mensagem.tipo === 'localizacao') return false;
      if (!this.mensagem.conteudo) return false;
      if (this.eMensagemContato && this.contatosFormatados.length) return false;
      return true;
    },
    mensagemRespondida() {
      const id = this.mensagem.respondendo_a_mensagem_id;
      if (!id) return null;
      return (this.mensagensDaAtiva || []).find((m) => m.id === id) || null;
    },
    previaResposta() {
      const m = this.mensagemRespondida;
      if (!m) return this.mensagem.respondendo_a_external_id ? '\u21A9 Resposta a uma mensagem' : null;
      if (m.conteudo) {
        let t = m.conteudo;
        if (t.length > 80) t = `${t.slice(0, 80)}\u2026`;
        return t;
      }
      const labels = {
        imagem: '\u{1F4F7} Imagem', audio: '\u{1F3A4} \u00C1udio', video: '\u{1F3AC} V\u00EDdeo',
        documento: '\u{1F4C4} Documento', localizacao: '\u{1F4CD} Localiza\u00E7\u00E3o', template: '\u{1F4CB} Template',
      };
      return labels[m.tipo] || '\u21A9 Mensagem';
    },
    autorResposta() {
      const m = this.mensagemRespondida;
      if (!m) return '';
      return m.direcao === 'entrada' ? 'Filiado' : 'Voc\u00EA';
    },
  },
  data() {
    return {
      menuAberto: false,
      copiado: false,
      // Contato compartilhado: estado da auto-tentativa de inserção/lookup.
      // 'idle' = ainda não tentou; 'verificando' = chamando API;
      // 'pronto' = tem ID na nossa base (achou existente OU criou);
      // 'incompleto' = falhou (precisa do modal de cadastro manual).
      contatoState: 'idle',
      contatoBaseId: null,
      contatoModalAberto: false,
      iniciarConversaModalAberto: false,
      // Modal de conflito: outro atendente já está com a conversa.
      conflitoModalAberto: false,
      conflitoAtendente: null,
      // Modal de visualização de vídeo
      videoModalAberto: false,
    };
  },
  mounted() {
    document.addEventListener('click', this._fecharMenuFora);
    if (this.eMensagemContato) this._verificarOuCriarContato();
  },
  beforeUnmount() {
    document.removeEventListener('click', this._fecharMenuFora);
  },
  methods: {
    ...mapActions('atendimento', ['setRespondendoA']),
    toggleMenu(e) {
      e.stopPropagation();
      this.menuAberto = !this.menuAberto;
    },
    _fecharMenuFora(e) {
      if (this.menuAberto && this.$refs.menuBox && !this.$refs.menuBox.contains(e.target)
          && !(this.$refs.menuBtn && this.$refs.menuBtn.contains(e.target))) {
        this.menuAberto = false;
      }
    },

    abrirVideoModal() { this.videoModalAberto = true; },
    fecharVideoModal() {
      this.videoModalAberto = false;
      // Pausa o vídeo do modal ao fechar
      const v = this.$refs.videoModalEl;
      if (v && typeof v.pause === 'function') v.pause();
    },

    // ── Contato compartilhado ─────────────────────────────────────────
    _telefoneCru() {
      const c = this.contatosFormatados[0];
      const tel = (c && c.telefones && c.telefones[0]) || '';
      return tel.replace(/\D/g, '');
    },
    /**
     * Normaliza telefone BR para comparação: remove DDI 55, remove o "9"
     * extra do celular novo formato (DDD9XXXX-XXXX → DDDXXXX-XXXX).
     * Resultado tem sempre 10 dígitos (DDD + 8) — assim "9807-9100" e
     * "99807-9100" do mesmo celular batem.
     */
    _normalizarTelefoneBR(numero) {
      let n = String(numero || '').replace(/\D/g, '');
      if ((n.length === 13 || n.length === 12) && n.startsWith('55')) n = n.slice(2);
      if (n.length === 11 && n[2] === '9') n = n.slice(0, 2) + n.slice(3);
      return n;
    },
    _payloadCadastro() {
      // Monta um objeto compatível com a prop `contato` do ContatoModal
      // (sem `id` → o modal entra em modo "criar").
      const c = this.contatosFormatados[0];
      if (!c) return null;
      const partes = String(c.nome || '').trim().split(/\s+/);
      const nome = partes[0] || '';
      const sobrenome = partes.slice(1).join(' ') || '';
      const telefone = this._telefoneCru();
      const email = (c.emails && c.emails[0]) || '';
      const canais = [];
      if (telefone) canais.push({ tipo_canal: 'telefone', valor_canal: telefone, principal: true });
      if (email) canais.push({ tipo_canal: 'email', valor_canal: email, principal: !telefone });
      return { nome, sobrenome, canais };
    },
    async _verificarOuCriarContato() {
      const c = this.contatosFormatados[0];
      const numero = this._telefoneCru();
      if (!c || !numero) { this.contatoState = 'incompleto'; return; }
      this.contatoState = 'verificando';
      const numeroNorm = this._normalizarTelefoneBR(numero);
      try {
        // 1) Busca existente por telefone. Manda BOTH variantes (com e sem o
        //    "9" do celular) pra aumentar chance do filtro do backend pegar.
        const variantesBusca = new Set();
        variantesBusca.add(numero);
        variantesBusca.add(numeroNorm);
        // Variante com "9" extra inserido após o DDD (caso o original venha sem).
        if (numeroNorm.length === 10) {
          variantesBusca.add(`${numeroNorm.slice(0, 2)}9${numeroNorm.slice(2)}`);
        }
        let arr = [];
        for (const v of variantesBusca) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await axios.get('/contato', { params: { telefone: v, limit: 10 } });
          const lista = (data && data.data) || data || [];
          arr = arr.concat(Array.isArray(lista) ? lista : (lista.rows || []));
          if (arr.length) break;
        }
        // Compara aplicando normalização BR nos dois lados — assim
        // "9807-9100" e "99807-9100" (mesmo celular, com/sem 9) batem.
        const existente = arr.find((ct) => (ct.canais || []).some((ch) => {
          if (ch.tipo_canal !== 'telefone') return false;
          return this._normalizarTelefoneBR(ch.normalizado || ch.valor_canal) === numeroNorm;
        }));
        if (existente && existente.id) {
          this.contatoBaseId = existente.id;
          this.contatoState = 'pronto';
          return;
        }
      } catch (_e) { /* segue para tentativa de criar */ }
      // 2) tenta criar automaticamente com o mínimo (nome + telefone)
      try {
        const payload = this._payloadCadastro();
        if (!payload || !payload.nome) { this.contatoState = 'incompleto'; return; }
        const resp = await axios.post('/contato', payload);
        const novo = (resp.data && resp.data.data) || resp.data;
        if (novo && novo.id) {
          this.contatoBaseId = novo.id;
          this.contatoState = 'pronto';
        } else {
          this.contatoState = 'incompleto';
        }
      } catch (_e) {
        // erro de validação, duplicidade não detectada, etc → modal manual
        this.contatoState = 'incompleto';
      }
    },
    async abrirConversaContato() {
      const telefone = this._telefoneCru();
      if (!this.contatoBaseId && !telefone) return;
      // Se o contato compartilhado é o mesmo da conversa em foco, não faz nada.
      const conversaAtiva = this.$store.getters['atendimento/conversaAtiva'];
      if (conversaAtiva && conversaAtiva.contato_id === this.contatoBaseId) return;
      try {
        // Mandamos contato_id E telefone — o backend re-valida pelo telefone
        // normalizado (ignora "9" extra do celular BR) pra evitar criar
        // conversa duplicada quando o contato_id local aponta pra um
        // possível duplicado.
        const result = await this.$store.dispatch('atendimento/abrirConversaCom', {
          contatoId: this.contatoBaseId,
          telefone,
        });
        if (result && result.conflito) {
          this.conflitoAtendente = result.atendente || null;
          this.conflitoModalAberto = true;
        }
      } catch (_e) {
        this.iniciarConversaModalAberto = true;
      }
    },
    abrirCadastroContato() {
      this.contatoModalAberto = true;
    },
    aoSalvarContato(novo) {
      const id = (novo && novo.id) || null;
      if (id) {
        this.contatoBaseId = id;
        this.contatoState = 'pronto';
      }
      this.contatoModalAberto = false;
    },

    responder() {
      this.setRespondendoA(this.mensagem);
      this.menuAberto = false;
    },
    async copiar() {
      try {
        const texto = this.eMensagemContato
          ? this.infoContato.copiar
          : this.mensagem.conteudo
          || (this.midiaUrl || '')
          || (this.linkMaps || '');
        await navigator.clipboard.writeText(texto || '');
        this.copiado = true;
        setTimeout(() => { this.copiado = false; }, 1500);
      } catch (_e) { /* ignore */ }
      this.menuAberto = false;
    },
    reagir() {
      this.$emit('reagir', this.mensagem);
      this.menuAberto = false;
    },
    encaminhar() {
      this.$emit('encaminhar', this.mensagem);
      this.menuAberto = false;
    },
    fixar() {
      this.$emit('fixar', this.mensagem);
      this.menuAberto = false;
    },
    favoritar() {
      this.$emit('favoritar', this.mensagem);
      this.menuAberto = false;
    },
    apagar() {
      this.$emit('apagar', this.mensagem);
      this.menuAberto = false;
    },
    linkify,
    resolveAssetUrl(relativeUrl) {
      if (!relativeUrl) return '';
      if (/^(https?:|data:)/i.test(relativeUrl)) return relativeUrl;
      const base = resolveUrl(process.env.VUE_APP_APIURL) || '';
      return `${base.replace(/\/$/, '')}${relativeUrl}`;
    },
    // Imagem do card de link: usa o thumbnail salvo; se faltar, deriva do
    // YouTube (thumbnail determinístico pelo ID). Igual aos grupos.
    linkThumb(preview) {
      if (!preview) return '';
      if (preview.thumbnail_url) return this.resolveAssetUrl(preview.thumbnail_url);
      return this.youtubeThumb(preview.canonical_url || preview.url || preview.matched_text);
    },
    youtubeThumb(url) {
      if (!url) return '';
      try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./i, '');
        let id = null;
        if (host === 'youtu.be') id = u.pathname.slice(1).split('/')[0];
        else if (/(^|\.)youtube\.com$/i.test(host)) {
          id = u.searchParams.get('v') || (u.pathname.match(/\/(?:shorts|embed|live)\/([^/?#]+)/) || [])[1];
        }
        if (id && /^[\w-]{6,}$/.test(id)) return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
      } catch (_e) { /* url inválida */ }
      return '';
    },
    // Mini-mapa GRÁTIS (sem chave) com tiles do OpenStreetMap centralizados na
    // coordenada da mensagem. Se um tile falhar, some só ele. Igual aos grupos.
    mapaTiles() {
      const m = this.midiaMeta;
      if (!m || m.latitude == null || m.longitude == null) return [];
      const lat = Number(m.latitude);
      const lng = Number(m.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return [];
      const z = 15;
      const TS = 256; // tamanho do tile
      const W = 330;
      const H = 150; // área visível do mapa
      const n = 2 ** z;
      const px = ((lng + 180) / 360) * n * TS;
      const latRad = (lat * Math.PI) / 180;
      const py = (0.5 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / (2 * Math.PI)) * n * TS;
      const originX = px - W / 2;
      const originY = py - H / 2;
      const tiles = [];
      for (let tx = Math.floor(originX / TS); tx <= Math.floor((originX + W) / TS); tx += 1) {
        for (let ty = Math.floor(originY / TS); ty <= Math.floor((originY + H) / TS); ty += 1) {
          if (ty < 0 || ty >= n) continue;
          const xt = ((tx % n) + n) % n;
          tiles.push({
            url: `https://tile.openstreetmap.org/${z}/${xt}/${ty}.png`,
            left: Math.round(tx * TS - originX),
            top: Math.round(ty * TS - originY),
          });
        }
      }
      return tiles;
    },
    _formatarTelefoneContato(valor) {
      const texto = String(valor || '').trim();
      if (!texto) return '';
      const digitos = texto.replace(/\D/g, '');
      const comPais = digitos.match(/^(\d{2})(\d{2})(\d{4,5})(\d{4})$/);
      if (comPais) return `+${comPais[1]} (${comPais[2]}) ${comPais[3]}-${comPais[4]}`;
      const brasil = digitos.match(/^(\d{2})(\d{4,5})(\d{4})$/);
      if (brasil) return `(${brasil[1]}) ${brasil[2]}-${brasil[3]}`;
      return texto;
    },
  },
};
</script>

<template>
  <!-- Sistema: pill central -->
  <div v-if="eSistema" class="wa-msg-system">
    <span class="wa-msg-system-pill">
      <i class="ri-information-line"></i> {{ mensagem.conteudo }}
    </span>
  </div>

  <div
    v-else
    class="wa-msg-row"
    :class="eEntrada ? 'wa-msg-row--in' : 'wa-msg-row--out'"
  >
    <div
      class="wa-bubble"
      :class="[
        eEntrada ? 'wa-bubble--in' : 'wa-bubble--out',
        {
          'wa-bubble--audio': mensagem.tipo === 'audio' && midiaUrl,
          'wa-bubble--card': temLinkCard || temLocalizacaoMapa,
          'wa-bubble--metasub': overlayHoraSub,
        },
      ]"
    >
      <!-- Tail (rabinho do bal\u00E3o) -->
      <span class="wa-tail" :class="eEntrada ? 'wa-tail--in' : 'wa-tail--out'"></span>

      <!-- Chevron menu (vis\u00EDvel ao hover) -->
      <button ref="menuBtn" type="button" class="wa-chevron" @click.stop="toggleMenu">
        <i class="ri-arrow-down-s-line"></i>
      </button>
      <div v-if="menuAberto" ref="menuBox" class="wa-menu" :class="eEntrada ? 'wa-menu--left' : 'wa-menu--right'" @click.stop>
        <button type="button" class="wa-menu-item" @click="responder">
          <i class="ri-reply-line"></i>Responder
        </button>
        <button type="button" class="wa-menu-item" @click="copiar">
          <i class="ri-file-copy-line"></i>{{ copiado ? 'Copiado!' : 'Copiar' }}
        </button>
        <button type="button" class="wa-menu-item" @click="reagir">
          <i class="ri-emotion-happy-line"></i>Reagir
        </button>
        <button type="button" class="wa-menu-item" @click="encaminhar">
          <i class="ri-share-forward-line"></i>Encaminhar
        </button>
        <div class="wa-menu-sep"></div>
        <button type="button" class="wa-menu-item" @click="fixar">
          <i class="ri-pushpin-line"></i>Fixar
        </button>
        <button type="button" class="wa-menu-item" @click="favoritar">
          <i class="ri-star-line"></i>Favoritar
        </button>
        <div class="wa-menu-sep"></div>
        <button type="button" class="wa-menu-item wa-menu-item--danger" @click="apagar">
          <i class="ri-delete-bin-line"></i>Apagar
        </button>
      </div>

      <!-- Quote box (reply) -->
      <div v-if="previaResposta" class="wa-quote" :class="eEntrada ? 'wa-quote--in' : 'wa-quote--out'">
        <div class="wa-quote-author">{{ autorResposta || 'Mensagem' }}</div>
        <div class="wa-quote-text">{{ previaResposta }}</div>
      </div>

      <!-- Imagem -->
      <a v-if="mensagem.tipo === 'imagem' && midiaUrl" :href="midiaUrl" target="_blank" class="wa-media-link wa-media-wrap">
        <img :src="midiaUrl" alt="imagem" class="wa-media-img" />
        <span v-if="overlayHoraDark" class="wa-meta-overlay">
          <span>{{ horario }}</span>
          <i
            v-if="!eEntrada && iconeStatus"
            :class="iconeStatus.icone"
            :style="{ color: iconeStatus.cor }"
            :title="iconeStatus.titulo"
          ></i>
        </span>
      </a>

      <!-- V\u00EDdeo: thumbnail quadrado com bot\u00E3o de play; ao clicar, abre modal -->
      <button
        v-else-if="mensagem.tipo === 'video' && midiaUrl"
        type="button"
        class="wa-media-video-thumb"
        :title="'Reproduzir v\u00EDdeo'"
        @click="abrirVideoModal"
      >
        <video
          :src="midiaUrl"
          preload="metadata"
          muted
          playsinline
          class="wa-media-video-thumb-el"
          aria-hidden="true"
        ></video>
        <span class="wa-media-video-play"><i class="ri-play-fill"></i></span>
        <span v-if="overlayHoraDark" class="wa-meta-overlay">
          <span>{{ horario }}</span>
          <i
            v-if="!eEntrada && iconeStatus"
            :class="iconeStatus.icone"
            :style="{ color: iconeStatus.cor }"
            :title="iconeStatus.titulo"
          ></i>
        </span>
      </button>

      <!-- \u00C1udio -->
      <AudioPlayer
        v-else-if="mensagem.tipo === 'audio' && midiaUrl"
        :src="midiaUrl"
        class="wa-media-audio"
      >
        <template #footer-right>
          <div class="wa-meta-inline">
            <span>{{ horario }}</span>
            <i
              v-if="!eEntrada && iconeStatus"
              :class="iconeStatus.icone"
              :style="{ color: iconeStatus.cor }"
              :title="iconeStatus.titulo"
            ></i>
          </div>
        </template>
      </AudioPlayer>

      <!-- Documento -->
      <a v-else-if="mensagem.tipo === 'documento' && midiaUrl" :href="midiaUrl" target="_blank" class="wa-doc">
        <div class="wa-doc-icon"><i class="ri-file-line"></i></div>
        <div class="wa-doc-info">
          <div class="wa-doc-name">{{ nomeArquivo }}</div>
          <div class="wa-doc-size">{{ tamanhoArquivo }}</div>
        </div>
        <i class="ri-download-2-line wa-doc-dl"></i>
      </a>

      <!-- Localiza\u00E7\u00E3o: card com mapa real (tiles OSM), igual aos grupos -->
      <a v-else-if="temLocalizacaoMapa" :href="hrefLocalizacao" target="_blank" class="wa-link-preview wa-loc-preview">
        <div class="wa-loc-map">
          <img
            v-for="(t, i) in mapaTiles()"
            :key="i"
            :src="t.url"
            class="wa-loc-tile"
            :style="{ left: t.left + 'px', top: t.top + 'px' }"
            alt=""
            loading="lazy"
            @error="$event.target.style.visibility = 'hidden'"
          />
          <i class="ri-map-pin-2-fill wa-loc-pin"></i>
          <span class="wa-meta-overlay">
            <span>{{ horario }}</span>
            <i
              v-if="!eEntrada && iconeStatus"
              :class="iconeStatus.icone"
              :style="{ color: iconeStatus.cor }"
              :title="iconeStatus.titulo"
            ></i>
          </span>
        </div>
        <div v-if="midiaMeta.nome || midiaMeta.endereco" class="wa-loc-info">
          <div class="wa-loc-name text-truncate">{{ midiaMeta.nome || 'Localiza\u00E7\u00E3o compartilhada' }}</div>
          <div v-if="midiaMeta.endereco" class="wa-loc-addr text-truncate">{{ midiaMeta.endereco }}</div>
        </div>
      </a>

      <!-- Contato -->
      <div v-else-if="eMensagemContato && contatosFormatados.length" class="wa-contact-card">
        <div class="wa-contact-header">
          <div class="wa-contact-icon"><i class="ri-contacts-book-line"></i></div>
          <div class="wa-contact-header-info">
            <div class="wa-contact-title">{{ infoContato.titulo }}</div>
            <div class="wa-contact-subtitle">{{ infoContato.subtitulo }}</div>
          </div>
          <!-- Ação à direita: spinner enquanto verifica;
               se já está na base → abrir conversa;
               se falhou inserir → abrir modal de cadastro pré-preenchido. -->
          <button
            v-if="eEntrada"
            type="button"
            class="wa-doc-dl wa-contact-action"
            :disabled="contatoState === 'verificando' || eContatoDaConversaAtiva"
            :title="eContatoDaConversaAtiva
              ? 'Você já está nessa conversa'
              : contatoState === 'pronto'
                ? 'Abrir conversa com este contato'
                : contatoState === 'incompleto'
                  ? 'Cadastrar contato na base'
                  : 'Verificando...'"
            @click.stop="contatoState === 'pronto' ? abrirConversaContato() : abrirCadastroContato()"
          >
            <i v-if="contatoState === 'verificando'" class="ri-loader-4-line wa-spin"></i>
            <i v-else-if="contatoState === 'pronto'" class="ri-chat-3-line"></i>
            <i v-else class="ri-user-add-line"></i>
          </button>
        </div>
      </div>

      <!-- M\u00EDdia indispon\u00EDvel -->
      <div
        v-else-if="['imagem','video','audio','documento'].includes(mensagem.tipo) && !midiaUrl"
        class="wa-doc wa-doc--fail"
      >
        <div class="wa-doc-icon"><i :class="iconeMidia"></i></div>
        <div class="wa-doc-info">
          <div class="wa-doc-name">{{ rotuloMidia }} indispon\u00EDvel</div>
          <div class="wa-doc-size">Falha ao baixar m\u00EDdia da Meta</div>
        </div>
        <i class="ri-error-warning-line wa-doc-dl" style="color: #e74c3c;"></i>
      </div>

      <!-- Template -->
      <div v-else-if="mensagem.tipo === 'template'" class="wa-template">
        <i class="ri-file-list-2-line"></i> {{ mensagem.conteudo }}
      </div>

      <!-- Link preview (texto com URL detectada pelo backend) -->
      <a
        v-if="temLinkCard"
        :href="linkPreview.url || linkPreview.canonical_url"
        target="_blank"
        class="wa-link-preview"
      >
        <img
          v-if="linkThumb(linkPreview)"
          :src="linkThumb(linkPreview)"
          class="wa-link-thumb"
          alt=""
          @error="$event.target.remove()"
        />
        <div class="wa-link-body">
          <div v-if="linkPreview.title" class="wa-link-title">{{ linkPreview.title }}</div>
          <div v-if="linkPreview.description" class="wa-link-desc">{{ linkPreview.description }}</div>
          <div class="wa-link-url text-truncate">{{ linkPreview.canonical_url || linkPreview.url }}</div>
        </div>
      </a>

      <!-- Texto / caption (URLs viram links clicáveis) -->
      <div
        v-if="mostrarTextoPrincipal"
        class="wa-text"
        :class="{ 'wa-text--with-media': mensagem.tipo !== 'texto' && midiaUrl }"
        v-html="linkify(mensagem.conteudo)"
      ></div>

      <!-- Footer: hora + status (embaixo, quando não há overlay) -->
      <div v-if="mostrarFooter" class="wa-footer">
        <span class="wa-time">{{ horario }}</span>
        <i
          v-if="!eEntrada && iconeStatus"
          :class="iconeStatus.icone"
          class="wa-status-icon"
          :style="{ color: iconeStatus.cor }"
          :title="iconeStatus.titulo"
        ></i>
      </div>

      <!-- Overlay de hora p/ card de link (reduz altura, igual grupos) -->
      <div v-if="overlayHoraSub" class="wa-meta-overlay--sub">
        <span>{{ horario }}</span>
        <i
          v-if="!eEntrada && iconeStatus"
          :class="iconeStatus.icone"
          :style="{ color: iconeStatus.cor }"
          :title="iconeStatus.titulo"
        ></i>
      </div>
    </div>

    <!-- Modal: cadastro do contato compartilhado pré-preenchido -->
    <ContatoModal
      v-if="contatoModalAberto"
      :visivel="contatoModalAberto"
      :contato="_payloadCadastro()"
      @fechar="contatoModalAberto = false"
      @salvo="aoSalvarContato"
    />

    <!-- Modal: iniciar nova conversa com o contato já cadastrado -->
    <IniciarConversaModal
      v-if="iniciarConversaModalAberto"
      @close="iniciarConversaModalAberto = false"
    />

    <!-- Modal: conflito — conversa já em andamento com outro atendente -->
    <div
      v-if="conflitoModalAberto"
      class="conflito-modal-overlay"
      @click.self="conflitoModalAberto = false"
    >
      <div class="conflito-modal" role="dialog" aria-modal="true">
        <header class="conflito-modal-header">
          <i class="ri-error-warning-line conflito-icon"></i>
          <h6 class="m-0">Conversa em andamento</h6>
        </header>
        <div class="conflito-modal-body">
          <p class="mb-2">
            Já existe uma conversa em andamento com este contato, sob atendimento de:
          </p>
          <div v-if="conflitoAtendente" class="conflito-atendente">
            <i class="ri-user-3-line"></i>
            <div>
              <div class="conflito-atendente-nome">{{ conflitoAtendente.nome }}</div>
              <div class="conflito-atendente-id">ID: #{{ conflitoAtendente.id }}</div>
            </div>
          </div>
          <p class="small text-muted mt-3 mb-1">Para atender este contato, você pode:</p>
          <ul class="small mb-0 ps-3">
            <li>Solicitar ao atendente que <strong>finalize</strong> a conversa atual</li>
            <li>Solicitar ao atendente que <strong>transfira</strong> o atendimento para você</li>
            <li>Contatar o <strong>gestor do sistema</strong> para redirecionar este contato</li>
          </ul>
        </div>
        <footer class="conflito-modal-footer">
          <button type="button" class="btn btn-primary" @click="conflitoModalAberto = false">
            Entendi
          </button>
        </footer>
      </div>
    </div>

    <!-- Modal de visualização de vídeo (Teleport pra fugir do overflow do bubble) -->
    <Teleport to="body">
      <div
        v-if="videoModalAberto"
        class="wa-video-modal-overlay"
        @click.self="fecharVideoModal"
      >
        <button
          type="button"
          class="wa-video-modal-close"
          title="Fechar"
          @click="fecharVideoModal"
        >
          <i class="ri-close-line"></i>
        </button>
        <video
          ref="videoModalEl"
          :src="midiaUrl"
          controls
          autoplay
          playsinline
          class="wa-video-modal-player"
        ></video>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* ===================================================================
   WhatsApp Web — Message Bubbles
   =================================================================== */

/* --- System message ---------------------------------------------- */
.wa-msg-system {
  display: flex;
  justify-content: center;
  padding: 4px 0;
  margin: 8px 0;
}
.wa-msg-system-pill {
  background: #fdf4c5;
  color: #54656f;
  font-size: 12px;
  padding: 5px 12px;
  border-radius: 8px;
  box-shadow: 0 1px 1px rgba(0,0,0,0.06);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.wa-msg-system-pill i { font-size: 13px; }

/* --- Message row ------------------------------------------------- */
.wa-msg-row {
  display: flex;
  padding: 0 7%;
  margin-bottom: 2px;
}
.wa-msg-row--in { justify-content: flex-start; }
.wa-msg-row--out { justify-content: flex-end; }

/* --- Bubble ------------------------------------------------------ */
.wa-bubble {
  position: relative;
  max-width: 65%;
  min-width: 80px;
  border-radius: 8px;
  padding: 6px 7px 8px 9px;
  word-break: break-word;
  box-shadow: 0 1px 0.5px rgba(11,20,26,0.13);
}
.wa-bubble--in {
  /* var(--vz-secondary-bg) é o tom de "card" do Velzon: levemente mais
     claro que o body em dark, levemente mais escuro em light — dá
     elevação sem hardcode e segue o tema do app. */
  --wa-bubble-bg: var(--vz-secondary-bg);
  background: var(--wa-bubble-bg);
  color: var(--vz-body-color);
  border-top-left-radius: 0;
}
/* Balão da MINHA mensagem: tom claro da cor primária (segue o whitelabel via
   --vz-primary; fallback fixo pra navegador sem color-mix), texto escuro —
   mesmo padrão dos grupos / WhatsApp Web. */
.wa-bubble--out {
  --wa-bubble-bg: #f6e1e2;
  --wa-bubble-bg: color-mix(in srgb, var(--vz-primary, #971219) 13%, #fff);
  background: var(--wa-bubble-bg);
  color: var(--vz-body-color);
  border-top-right-radius: 0;
}
.wa-bubble--audio { min-width: 260px; }

/* --- Tail (rabinho) ---------------------------------------------- */
.wa-tail {
  position: absolute;
  top: 0;
  width: 8px;
  height: 13px;
}
.wa-tail--in {
  left: -8px;
  color: var(--wa-bubble-bg);
}
.wa-tail--out {
  right: -8px;
  color: var(--wa-bubble-bg);
}
.wa-tail::before {
  content: '';
  position: absolute;
  top: 0;
  width: 0;
  height: 0;
}
.wa-tail--in::before {
  right: 0;
  border-top: 0 solid transparent;
  border-right: 8px solid var(--wa-bubble-bg);
  border-bottom: 13px solid transparent;
}
.wa-tail--out::before {
  left: 0;
  border-top: 0 solid transparent;
  border-left: 8px solid var(--wa-bubble-bg);
  border-bottom: 13px solid transparent;
}

/* --- Chevron / menu button --------------------------------------- */
.wa-chevron {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--vz-secondary-color);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s, background 0.12s;
  z-index: 2;
}
.wa-bubble:hover .wa-chevron { opacity: 1; }
.wa-chevron:hover { background: rgba(0,0,0,0.08); }
/* Força a cor no <i> interno também — alguns resets globais do Velzon
   sobrescrevem a herança do <button>, deixando o ícone com cor padrão
   em vez de seguir o `color` do pai. Balões claros (entrada e saída) →
   chevron na cor secundária, legível nos dois. */
.wa-chevron,
.wa-chevron i { color: var(--vz-secondary-color); }

/* --- Context menu ------------------------------------------------ */
.wa-menu {
  position: absolute;
  top: 28px;
  z-index: 30;
  background: #fff;
  border-radius: 3px;
  box-shadow: 0 2px 5px rgba(11,20,26,0.26), 0 2px 10px rgba(11,20,26,0.16);
  min-width: 190px;
  padding: 8px 0;
}
.wa-menu--right { right: 0; }
.wa-menu--left { left: 0; }
.wa-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  border: none;
  background: transparent;
  color: #111b21;
  font-size: 14px;
  padding: 8px 22px;
  cursor: pointer;
  text-align: left;
}
.wa-menu-item:hover { background: #f5f6f6; }
.wa-menu-item i { font-size: 16px; color: #8696a0; }
.wa-menu-item--danger, .wa-menu-item--danger i { color: #e74c3c; }
.wa-menu-sep { height: 1px; background: #e9edef; margin: 4px 0; }

/* --- Quote box (reply) ------------------------------------------- */
.wa-quote {
  border-radius: 7px;
  padding: 5px 12px 7px;
  margin-bottom: 4px;
  border-left: 4px solid;
  cursor: pointer;
}
.wa-quote--in {
  background: rgba(var(--vz-primary-rgb, 200, 16, 46), 0.08);
  border-left-color: var(--vz-primary);
}
.wa-quote--out {
  background: rgba(var(--vz-primary-rgb, 200, 16, 46), 0.10);
  border-left-color: var(--vz-primary);
}
.wa-quote-author {
  font-size: 12.5px;
  font-weight: 500;
  margin-bottom: 1px;
}
.wa-quote--in .wa-quote-author { color: var(--vz-primary); }
.wa-quote--out .wa-quote-author { color: var(--vz-primary); }
.wa-quote-text {
  font-size: 13px;
  color: #667781;
  line-height: 1.3;
  word-break: break-word;
}
/* Saída agora é um balão CLARO (tom da primária), então o texto citado volta
   ao cinza secundário, legível sobre o tom rosado. */
.wa-quote--out .wa-quote-text { color: var(--vz-secondary-color); }

/* --- Text content ------------------------------------------------ */
.wa-text {
  white-space: pre-wrap;
  font-size: 14.2px;
  line-height: 19px;
  color: inherit;
  /* Reserva espa\u00E7o para o footer (hora+check) flutuar ao lado do texto */
  padding-right: 60px;
}
.wa-text--with-media { margin-top: 4px; }
/* Links dentro do texto (linkify): azul de link em entrada, tom da prim\u00E1ria em
   sa\u00EDda \u2014 leg\u00EDvel sobre o bal\u00E3o claro; URL longa quebra dentro do bal\u00E3o. */
.wa-text :deep(a) {
  color: #027eb5;
  text-decoration: underline;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.wa-bubble--out .wa-text :deep(a) { color: var(--vz-primary, #971219); }

/* --- Footer (hora + check) --------------------------------------- */
.wa-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  /* Flutua no canto inferior direito, sobrepondo levemente o padding do texto */
  float: right;
  margin-top: -12px;
  margin-bottom: -3px;
  position: relative;
}
.wa-time {
  font-size: 11px;
  color: #667781;
  line-height: 1;
}
.wa-status-icon {
  font-size: 16px;
  line-height: 1;
  /* Tom neutro (igual grupos); a falha recebe vermelho via style inline. */
  color: var(--vz-secondary-color);
}
/* Saída agora é balão CLARO → hora cinza discreta e texto na cor do corpo. */
.wa-bubble--out .wa-time { color: rgba(0, 0, 0, 0.45); }
.wa-bubble--out .wa-text { color: var(--vz-body-color); }

/* --- Audio meta -------------------------------------------------- */
.wa-meta-inline {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  font-size: 11px;
  width: 100%;
  /* Cor herda do contexto da bolha — entrada (claro) usa cor secundária,
     saída (vermelho) sobrescreve abaixo pra branco translúcido. */
  color: var(--vz-secondary-color);
}
.wa-bubble--out .wa-meta-inline { color: var(--vz-secondary-color); }
:global([data-bs-theme="dark"]) .wa-bubble--in .wa-meta-inline {
  color: rgba(255, 255, 255, 0.65);
}

/* --- Media: imagem, video ---------------------------------------- */
.wa-media-link { display: block; text-decoration: none; }
.wa-media-img {
  max-width: 330px;
  max-height: 330px;
  border-radius: 6px;
  display: block;
  cursor: pointer;
}
/* Vídeo: thumbnail quadrado com botão de play (estilo WhatsApp) */
.wa-media-video-thumb {
  position: relative;
  display: block;
  width: 260px;
  height: 260px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  overflow: hidden;
  background: #000;
  cursor: pointer;
}
.wa-media-video-thumb-el {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
}
.wa-media-video-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  transition: background 0.15s;
}
.wa-media-video-thumb:hover .wa-media-video-play { background: rgba(0, 0, 0, 0.4); }
.wa-media-video-play i {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: #1f1f1f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

/* Modal de reprodução de vídeo */
:global(.wa-video-modal-overlay) {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}
:global(.wa-video-modal-player) {
  max-width: 92vw;
  max-height: 88vh;
  border-radius: 6px;
  background: #000;
  outline: none;
}
:global(.wa-video-modal-close) {
  position: absolute;
  top: 1rem;
  right: 1.25rem;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 0;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 1.6rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
:global(.wa-video-modal-close:hover) {
  background: rgba(255, 255, 255, 0.22);
}
.wa-media-audio { width: 100%; max-width: 100%; display: flex; }

/* --- Media wrapper + overlay de hora (padrão dos grupos) ---------- */
.wa-media-wrap { position: relative; display: inline-block; border-radius: 6px; overflow: hidden; }
/* Faixa de largura total no rodapé da mídia/mapa: gradiente escuro + hora
   branca à direita (estilo WhatsApp Web). */
.wa-meta-overlay {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 3;
  display: flex; justify-content: flex-end; align-items: flex-end; gap: 3px;
  color: #fff; font-size: 11px; line-height: 1;
  padding: 20px 8px 5px;
  background: linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 45%, transparent 100%);
  text-shadow: 0 1px 2px rgba(0,0,0,0.65);
  pointer-events: none;
}
.wa-meta-overlay i { font-size: 14px; }

/* Overlay sutil p/ cards de corpo claro (link preview): a hora ocupa a linha de
   baixo sobre um gradiente que funde no fundo do balão — reduz a altura. */
.wa-meta-overlay--sub {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 3;
  display: flex; justify-content: flex-end; align-items: flex-end; gap: 3px;
  font-size: 11px; line-height: 1; color: var(--vz-secondary-color);
  padding: 16px 8px 5px;
  background: linear-gradient(to top, var(--wa-bubble-bg) 20%, transparent 100%);
  pointer-events: none; border-radius: 0 0 8px 8px;
}
.wa-meta-overlay--sub i { font-size: 13px; }

/* Cards que não são texto puro (link, localização): largura padrão do balão e
   quase rentes às bordas — igual WhatsApp Web / grupos. */
.wa-bubble--card { width: 330px; max-width: 100%; padding: 3px; }
.wa-bubble--card .wa-text { padding: 4px 6px 0; }
.wa-bubble--metasub { padding-bottom: 18px; }
.wa-bubble--card.wa-bubble--metasub .wa-text { padding-bottom: 0; }

/* --- Link preview (card de link nas mensagens de texto) ----------- */
.wa-link-preview {
  display: flex; flex-direction: column;
  border-radius: 8px; overflow: hidden;
  background: rgba(0, 0, 0, 0.05); color: inherit;
  text-decoration: none !important; transition: background 0.15s;
}
.wa-link-preview:hover { background: rgba(0, 0, 0, 0.08); }
.wa-link-thumb {
  width: 100%; aspect-ratio: 1.91 / 1; max-height: 188px;
  object-fit: cover; display: block; background: rgba(0, 0, 0, 0.06);
}
.wa-link-body { padding: 8px 10px; }
.wa-link-title {
  font-weight: 600; font-size: 13.5px; line-height: 1.3; margin-bottom: 2px;
  color: var(--vz-body-color);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.wa-link-desc {
  font-size: 12.5px; color: var(--vz-secondary-color); line-height: 1.3; margin-bottom: 2px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.wa-link-url {
  font-size: 11.5px; color: var(--vz-secondary-color); text-transform: lowercase;
}

/* --- Localização: mini-mapa com tiles OSM (sem chave) ------------- */
.wa-loc-map {
  position: relative; width: 100%; height: 140px;
  display: flex; align-items: center; justify-content: center;
  /* Fundo estilizado (fallback quando os tiles não carregam). */
  background:
    repeating-linear-gradient(0deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 26px),
    repeating-linear-gradient(90deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 26px),
    linear-gradient(135deg, #cfe8d6, #a9d2c3);
  overflow: hidden;
}
.wa-loc-tile { position: absolute; width: 256px; height: 256px; max-width: none; user-select: none; pointer-events: none; }
.wa-loc-pin {
  position: relative; z-index: 1; font-size: 2.2rem; color: #e11d48;
  filter: drop-shadow(0 2px 3px rgba(0,0,0,0.35)); transform: translateY(-4px);
}
.wa-loc-info { padding: 6px 10px 7px; }
.wa-loc-name { font-size: 13px; font-weight: 500; color: var(--vz-body-color); }
.wa-loc-addr { font-size: 11.5px; color: var(--vz-secondary-color); margin-top: 1px; }

/* --- Document card ----------------------------------------------- */
.wa-doc {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  text-decoration: none;
  color: inherit;
  min-width: 220px;
  /* Card interno neutro (mesma cor em entrada e saída), igual aos grupos —
     sem realce primário por direção. */
  background: rgba(0, 0, 0, 0.04);
}
.wa-doc--fail { opacity: 0.8; }
.wa-doc-icon {
  width: 38px;
  height: 38px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.08);
  color: var(--vz-secondary-color);
}
.wa-doc-info { flex: 1; min-width: 0; }
.wa-doc-name {
  font-size: 13.5px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--vz-body-color);
}
.wa-doc-size { font-size: 11.5px; color: var(--vz-secondary-color); margin-top: 1px; }
.wa-doc-dl { font-size: 20px; color: var(--vz-secondary-color); flex-shrink: 0; }

/* Botão de ação à direita do card de contato (chat/cadastrar/spinner) */
.wa-contact-action {
  background: transparent;
  border: 0;
  padding: 6px;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.wa-contact-action:hover:not(:disabled) {
  background: rgba(var(--vz-primary-rgb, 200, 16, 46), 0.14);
  color: var(--vz-primary);
}
.wa-contact-action:disabled { opacity: 0.6; cursor: progress; }
.wa-spin { display: inline-block; animation: wa-spin-rot 0.9s linear infinite; }
@keyframes wa-spin-rot { to { transform: rotate(360deg); } }

/* --- Modal de conflito (conversa com outro atendente) ------------- */
.conflito-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
}
.conflito-modal {
  background: var(--vz-body-bg);
  color: var(--vz-body-color);
  border-radius: 8px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
}
.conflito-modal-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--vz-border-color);
}
.conflito-icon { font-size: 1.4rem; color: #f0a500; }
.conflito-modal-body { padding: 1rem; }
.conflito-atendente {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.75rem;
  background: rgba(var(--vz-primary-rgb, 200, 16, 46), 0.08);
  border-left: 4px solid var(--vz-primary);
  border-radius: 4px;
}
.conflito-atendente i { font-size: 1.4rem; color: var(--vz-primary); }
.conflito-atendente-nome { font-weight: 600; font-size: 0.95rem; }
.conflito-atendente-id { font-size: 0.75rem; color: var(--vz-secondary-color); }
.conflito-modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--vz-border-color);
}
/* Balão de saída agora é claro → doc herda as mesmas cores da entrada. */
.wa-bubble--out .wa-doc-name { color: var(--vz-body-color); }
.wa-bubble--out .wa-doc-size,
.wa-bubble--out .wa-doc-dl { color: var(--vz-secondary-color); }

/* --- Contact card ------------------------------------------------ */
.wa-contact-card {
  min-width: 240px;
  border-radius: 6px;
  padding: 10px;
  /* Card interno neutro (igual em entrada e saída), padrão dos grupos. */
  background: rgba(0, 0, 0, 0.04);
}
.wa-contact-header {
  display: flex;
  align-items: center;
  gap: 10px;
}
.wa-contact-icon {
  width: 38px;
  height: 38px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.08);
  color: var(--vz-secondary-color);
}
.wa-contact-header-info,
.wa-contact-info {
  min-width: 0;
  flex: 1;
}
.wa-contact-title {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--vz-body-color);
}
.wa-contact-subtitle {
  font-size: 11.5px;
  color: var(--vz-secondary-color);
  margin-top: 1px;
}
/* Balão de saída agora é claro → contato herda as cores da entrada. */
.wa-bubble--out .wa-contact-title { color: var(--vz-body-color); }
.wa-bubble--out .wa-contact-subtitle { color: var(--vz-secondary-color); }
.wa-contact-list {
  display: flex;
  flex-direction: column;
}
.wa-contact-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding-top: 10px;
}
.wa-contact-item + .wa-contact-item {
  margin-top: 10px;
  border-top: 1px solid rgba(0,0,0,0.08);
}
.wa-contact-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}
.wa-bubble--in .wa-contact-avatar {
  background: #d9dee3;
  color: #54656f;
}
.wa-bubble--out .wa-contact-avatar {
  background: rgba(255,255,255,0.5);
  color: #1b5e3a;
}
.wa-contact-name {
  font-size: 13.5px;
  font-weight: 500;
  color: #111b21;
  line-height: 1.3;
}
.wa-contact-line {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: #667781;
  font-size: 12px;
  line-height: 1.35;
  margin-top: 4px;
}
.wa-contact-line i {
  font-size: 14px;
  color: #8696a0;
  flex-shrink: 0;
  margin-top: 1px;
}
.wa-contact-line span {
  word-break: break-word;
}

/* --- Template ---------------------------------------------------- */
.wa-template {
  font-style: italic;
  font-size: 13.5px;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ===================================================================
   Dark mode
   =================================================================== */
/* Bolha de entrada: var(--vz-secondary-bg) já adapta sozinha em dark mode
   via Velzon — só ajustamos a sombra pra ficar visível em fundo escuro. */
:global([data-bs-theme="dark"]) .wa-bubble--in {
  box-shadow: 0 1px 0.5px rgba(0,0,0,0.3);
}
:global([data-bs-theme="dark"]) .wa-bubble--out {
  box-shadow: 0 1px 0.5px rgba(0,0,0,0.3);
}
:global([data-bs-theme="dark"]) .wa-text { color: #e9edef; }
:global([data-bs-theme="dark"]) .wa-bubble--out .wa-text { color: #e9edef; }
:global([data-bs-theme="dark"]) .wa-time { color: rgba(255,255,255,0.6); }
:global([data-bs-theme="dark"]) .wa-bubble--out .wa-time { color: rgba(255,255,255,0.6); }
:global([data-bs-theme="dark"]) .wa-chevron,
:global([data-bs-theme="dark"]) .wa-chevron i { color: #fff; }
:global([data-bs-theme="dark"]) .wa-bubble--out .wa-chevron,
:global([data-bs-theme="dark"]) .wa-bubble--out .wa-chevron i { color: #fff; }
:global([data-bs-theme="dark"]) .wa-menu {
  background: #233138;
  box-shadow: 0 2px 8px rgba(0,0,0,0.5);
}
:global([data-bs-theme="dark"]) .wa-menu-item { color: #e9edef; }
:global([data-bs-theme="dark"]) .wa-menu-item:hover { background: #182229; }
:global([data-bs-theme="dark"]) .wa-menu-item i { color: #aebac1; }
:global([data-bs-theme="dark"]) .wa-menu-sep { background: #2a3942; }
/* Dark mode: SEM :global — neste projeto `:global([data-bs-theme=dark]) .x`
   compila errado (descarta a classe e joga o estilo no <html>). Como o
   [data-bs-theme=dark] fica no <html> (ancestral), a regra scoped simples mira
   certo. Ver memória vue_global_dark_css_bug. */
[data-bs-theme="dark"] .wa-quote--in {
  background: rgba(255, 255, 255, 0.07);
  border-left-color: rgba(255, 255, 255, 0.35);
}
[data-bs-theme="dark"] .wa-quote--in .wa-quote-author { color: #e79aa1; }
[data-bs-theme="dark"] .wa-quote--in .wa-quote-text { color: #cbd5db; }

/* Cards internos das bolhas (doc / contato / localização). Aplicado direto
   sem qualificar pela direção pra garantir que o scoped CSS do Vue resolva
   corretamente o match. Cores escolhidas pra contrastar tanto sobre o
   fundo de bolha entrada (#252d35) quanto saída (var(--vz-primary)). */
:global([data-bs-theme="dark"]) .wa-doc,
:global([data-bs-theme="dark"]) .wa-contact-card {
  background: rgba(255,255,255,0.08);
}
:global([data-bs-theme="dark"]) .wa-doc-icon,
:global([data-bs-theme="dark"]) .wa-contact-icon,
:global([data-bs-theme="dark"]) .wa-contact-avatar {
  background: rgba(255,255,255,0.14);
  color: #aebac1;
}
:global([data-bs-theme="dark"]) .wa-contact-header,
:global([data-bs-theme="dark"]) .wa-contact-item + .wa-contact-item {
  border-color: rgba(255,255,255,0.1);
}

/* System pill ("Tipo não suportado", "Opt-out registrado", etc.) */
:global([data-bs-theme="dark"]) .wa-msg-system-pill {
  background: rgba(255,255,255,0.06);
  color: #aebac1;
  box-shadow: none;
}
:global([data-bs-theme="dark"]) .wa-meta-inline { color: #fff; }

/* ===================================================================
   Dark mode — regras NOVAS no padrão correto do projeto
   ([data-bs-theme] SEM :global; ver memória vue_global_dark_css_bug).
   =================================================================== */
/* Balão de saída em dark: tom escuro da primária + texto claro. As demais
   (doc/contato/link/hora) herdam via --wa-bubble-bg. */
[data-bs-theme="dark"] .wa-bubble--out {
  --wa-bubble-bg: color-mix(in srgb, var(--vz-primary, #971219) 42%, #1b2429);
  color: #e9edef;
}
[data-bs-theme="dark"] .wa-bubble--out .wa-text { color: #e9edef; }
[data-bs-theme="dark"] .wa-time,
[data-bs-theme="dark"] .wa-bubble--out .wa-time { color: rgba(255, 255, 255, 0.5); }

/* Cards internos neutros — no dark, tint claro (o rgba escuro sumiria). */
[data-bs-theme="dark"] .wa-doc,
[data-bs-theme="dark"] .wa-contact-card,
[data-bs-theme="dark"] .wa-link-preview { background: rgba(255, 255, 255, 0.07); }
[data-bs-theme="dark"] .wa-link-preview:hover { background: rgba(255, 255, 255, 0.11); }
[data-bs-theme="dark"] .wa-link-thumb { background: rgba(255, 255, 255, 0.05); }
[data-bs-theme="dark"] .wa-doc-icon,
[data-bs-theme="dark"] .wa-contact-icon,
[data-bs-theme="dark"] .wa-contact-avatar {
  background: rgba(255, 255, 255, 0.14);
  color: #aebac1;
}

/* Mapa da localização (fallback estilizado) no tema escuro. */
[data-bs-theme="dark"] .wa-loc-map {
  background:
    repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 26px),
    repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 26px),
    linear-gradient(135deg, #223129, #1a2b33);
}

/* Quote (reply) do balão de saída em dark — claro sobre o tom escuro. */
[data-bs-theme="dark"] .wa-quote--out {
  background: rgba(255, 255, 255, 0.09);
  border-left-color: rgba(255, 255, 255, 0.4);
}
[data-bs-theme="dark"] .wa-quote--out .wa-quote-author { color: #e79aa1; }
[data-bs-theme="dark"] .wa-quote--out .wa-quote-text { color: #cbd5db; }

/* Links no texto (linkify) em dark: tons CLAROS pra ler sobre o balão escuro —
   azul na entrada, rosa na saída (igual aos grupos). Sem isso, o azul/vermelho
   escuros do modo claro ficavam ilegíveis. */
[data-bs-theme="dark"] .wa-text :deep(a) { color: #53bdeb; }
[data-bs-theme="dark"] .wa-bubble--out .wa-text :deep(a) { color: #f0aeb4; }
</style>
